import { AssistantToolName, AssistantUITools } from "@repo/api/client";
import { Button, Flex, Icon, Text } from "@repo/ui";
import { getToolName, ToolUIPart } from "ai";
import { useEffect, type ReactNode } from "react";
import { type ConfirmToolCallFn } from "../../../ai/useAssistantChat";
import { ToolCallView } from "./ToolCallView";

type AssistantToolUIPart<
	ToolName extends AssistantToolName = AssistantToolName,
> = ToolUIPart<Pick<AssistantUITools, ToolName>>;

type ToolField = {
	label: string;
	value: ReactNode;
	optional?: boolean;
};

type ToolConfirmationConfig = {
	titleText: string;
	confirmActionText: string;
	extractFields: (input: any) => ToolField[];
};

const toolConfirmationConfig: Record<string, ToolConfirmationConfig> = {
	// update_provider_info: {
	// 	titleText: "Update provider information?",
	// 	confirmActionText: "Update Provider Info",
	// 	extractFields: (input) => {
	// 		const fields: ToolField[] = [];

	// 		if (input.provider) {
	// 			fields.push(
	// 				{ label: "Provider Name", value: input.provider.name },
	// 				{ label: "NPI", value: input.provider.npi },
	// 				{ label: "Specialty", value: input.provider.specialty },
	// 				{ label: "Phone", value: input.provider.phoneNumber },
	// 				{ label: "Fax", value: input.provider.faxNumber, optional: true },
	// 				{
	// 					label: "Email",
	// 					value: input.provider.emailAddress,
	// 					optional: true,
	// 				},
	// 				{
	// 					label: "Telehealth",
	// 					value: input.provider.offersTelehealth ? "Yes" : "No",
	// 					optional: true,
	// 				},
	// 			);
	// 		}

	// 		if (input.organization) {
	// 			fields.push(
	// 				{ label: "Organization", value: input.organization.companyName },
	// 				{ label: "Org NPI", value: input.organization.npi, optional: true },
	// 				{ label: "Org Type", value: input.organization.type, optional: true },
	// 			);
	// 		}

	// 		if (input.locations && input.locations.length > 0) {
	// 			input.locations.forEach((location: any, index: number) => {
	// 				const prefix =
	// 					input.locations.length > 1 ? `Location ${index + 1} ` : "";
	// 				fields.push(
	// 					{ label: `${prefix}Address`, value: location.address },
	// 					{ label: `${prefix}City`, value: location.city },
	// 					{ label: `${prefix}State`, value: location.state },
	// 					{ label: `${prefix}Phone`, value: location.phoneNumber },
	// 					{
	// 						label: `${prefix}Fax`,
	// 						value: location.faxNumber,
	// 						optional: true,
	// 					},
	// 				);
	// 			});
	// 		}

	// 		return fields;
	// 	},
	// },
	book_appointment_by_phone: {
		titleText: "Book phone appointment?",
		confirmActionText: "Book Appointment",
		extractFields: (input) => [
			{ label: "Patient", value: input.patientName },
			{ label: "DOB", value: input.patientDateOfBirth },
			{ label: "Phone", value: input.phoneNumber },
			{ label: "Insurance", value: input.insuranceName, optional: true },
			{ label: "Call Receiver", value: input.callReceiver, optional: true },
		],
	},
	check_patient_eligibility: {
		titleText: "Check patient eligibility?",
		confirmActionText: "Run Eligibility",
		extractFields: (_input) => [],
	},
	attach_records_send_referral: {
		titleText: "Send referral?",
		confirmActionText: "Send Referral",
		extractFields: (input) => [
			{ label: "Fax Number", value: input.provider.faxNumber },
			{ label: "Recipient", value: input.provider.name },
			{
				label: "Recipient Address",
				value: input.provider
					? `${input.provider.addressLine1}, ${input.provider.city}, ${input.provider.state} ${input.provider.zipCode}`
					: undefined,
			},
			{
				label: "Recipient Phone",
				value: input.provider.phoneNumber,
				optional: true,
			},
			{
				label: "Prior Authorization Required",
				value: input.priorAuthorizationDetails.priorAuthRequired ?? "Unknown",
			},
			{
				label: "Prior Auth Number",
				value: input.priorAuthorizationDetails?.authorizationNumber,
				optional: true,
			},
			{
				label: "Visits Approved",
				value: input.priorAuthorizationDetails?.numberOfVisitsAllowed,
				optional: true,
			},
		],
	},
	submit_referral_prior_authorization: {
		titleText: "Submit prior authorization?",
		confirmActionText: "Submit Prior Auth",
		extractFields: (input) => [
			{
				label: "Provider",
				value: `${input.provider?.firstName} ${input.provider?.lastName}`,
			},
			{ label: "NPI", value: input.provider?.npi },
			{
				label: "Address",
				value: `${input.provider?.addressLine1}, ${input.provider?.city}, ${input.provider?.state} ${input.provider?.zipCode}`,
			},
		],
	},
};

const renderMaybe = (field: ToolField): ReactNode => {
	const { label, value, optional } = field;
	// If field is required, we will always render the fields and put "Unknown" if the value is null/undefined/empty
	// If field is optional, we will only render the fields if the value is not null/undefined/empty
	if (
		optional &&
		(value === null ||
			value === undefined ||
			value === "" ||
			value === "undefined undefined")
	) {
		return null;
	}
	return (
		<Flex key={label} justify="space-between">
			<Text size="md" color="blue11" weight="medium">
				{label}:
			</Text>
			<Text size="md" color="blue12">
				{value !== null && value !== undefined ? String(value) : "Unknown"}
			</Text>
		</Flex>
	);
};

const ToolConfirmation: React.FC<{
	toolCall: AssistantToolUIPart;
	onApprove: (toolCallId: string) => void;
	onReject: (toolCallId: string) => void;
}> = ({ toolCall, onApprove, onReject }) => {
	const toolName = getToolName(toolCall) as AssistantToolName;
	const config = toolConfirmationConfig[toolName];

	// Keyboard handlers
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				onReject(toolCall.toolCallId);
			} else if (e.key === "Enter" && e.ctrlKey) {
				e.preventDefault();
				onApprove(toolCall.toolCallId);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [toolCall.toolCallId, onApprove, onReject]);

	if (!config) {
		return <ToolCallView part={toolCall} />;
	}

	const fields = config.extractFields(toolCall.input);
	const visibleFields = fields.map(renderMaybe).filter(Boolean);

	return (
		<Flex
			column
			gap="md"
			padding="lg"
			borderRadius="md"
			background="blue2"
			borderWidth="md"
			borderColor="blue6"
			flex
			style={{ maxWidth: "420px" }}
		>
			{/* Header */}
			<Flex align="flex-start" gap="xs">
				<Flex shrink={0} padding="xxxs">
					<Icon name="AlertCircle" size="sm" color="blue9" />
				</Flex>
				<Text size="md" color="blue12" weight="medium">
					{config.titleText}
				</Text>
			</Flex>

			{/* Field details */}
			{visibleFields.length > 0 && (
				<Flex
					column
					gap="xs"
					padding="md"
					borderRadius="sm"
					background="background"
					borderWidth="md"
					borderColor="blue4"
				>
					{visibleFields}
				</Flex>
			)}

			{/* Actions */}
			<Flex gap="xs" justify="space-between" align="center">
				<Button
					size="sm"
					variant="ghost"
					onPress={() => onReject(toolCall.toolCallId)}
				>
					<Flex align="center" gap="xs">
						<Icon name="X" size="xs" />
						<Text size="md">Cancel</Text>
					</Flex>
				</Button>
				<Button
					size="sm"
					variant="primary"
					onPress={() => onApprove(toolCall.toolCallId)}
				>
					<Flex align="center" gap="xs">
						<Icon name="Check" size="xs" />
						<Text size="md">{config.confirmActionText}</Text>
					</Flex>
				</Button>
			</Flex>
		</Flex>
	);
};

export const ToolConfirmationCard: React.FC<{
	part: AssistantToolUIPart;
	isPendingConfirmation: boolean;
	confirmToolCall: ConfirmToolCallFn;
}> = ({ part, isPendingConfirmation, confirmToolCall }) => {
	// Handle race condition - if tool finished while user was deciding
	if (!isPendingConfirmation || part.state !== "input-available") {
		return <ToolCallView part={part} />;
	}

	const handleApprove = (toolCallId: string) => {
		confirmToolCall(toolCallId, { permission: "approve" });
	};

	const handleReject = (toolCallId: string) => {
		const toolName = getToolName(part) as AssistantToolName;
		confirmToolCall(toolCallId, {
			permission: "reject",
			output: {
				success: false,
				code: "REJECTED_BY_USER",
				message: `User rejected the assistant's request to call tool '${toolName}'`,
			},
		});
	};

	return (
		<ToolConfirmation
			toolCall={part}
			onApprove={handleApprove}
			onReject={handleReject}
		/>
	);
};
